// @flow
import Vue from 'vue'
import { useModalsStore } from '@/stores/modals'
import { useUserAuthStore } from '@/stores/user-auth'
import * as errorTracker from '@/utils/error-tracker'

/**
 * LEGACY - use fbAPIPromise
 * make get request with Config with Vue Resource to a FB API URL
 * handles generic errors with modals for logged out and 500 errors
 *
 * @param url string - after the api/v1/
 * @param config Object pass to Vue Resource
 * @param successCallback
 * @param errorCallback
 */
export const fbAPIGetWithConfig = (
  url: string,
  config: Object,
  successCallback?: (res: any) => void,
  errorCallback?: (err: any) => void,
) => {
  fbAPICall('get', url, config, successCallback, errorCallback)
}

/**
 * LEGACY - use fbAPIPromise
 * make get request with Vue Resource to a FB API URL
 * handle generic errors with modals for logged out and 500 errors
 *
 * @param url string - after the api/v1/
 * @param successCallback
 * @param errorCallback
 */
export const fbAPIGet = (url: string, successCallback: Function, errorCallback: Function) => {
  fbAPICall('get', url, [], successCallback, errorCallback)
}

/**
 * LEGACY - use fbAPIPromise
 * make post request with Vue Resource to a FB API URL
 * handle generic errors with modals for logged out and 500 errors
 *
 * @param url - after the api/v1/
 * @param data - usually a window.FormData() object
 * @param successCallback
 * @param errorCallback
 */
export const fbAPIPost = (
  url: string,
  data?: any,
  successCallback?: (res: any) => void,
  errorCallback?: (err: any) => void,
) => {
  fbAPICall('post', url, data, successCallback, errorCallback)
}

/**
 * LEGACY - use fbAPIPromise
 * make post request with Vue Resource to a FB API URL
 * handle generic errors with modals for logged out and 500 errors
 *
 * @param url - after the api/v1/
 * @param data - usually a window.FormData() object
 * @param successCallback
 * @param errorCallback
 */
export const fbAPIPostMulti = (
  url: string,
  data: any,
  successCallback?: (res: any) => void,
  errorCallback?: (err: any) => void,
) => {
  fbAPICall('postMulti', url, data, successCallback, errorCallback)
}

/**
 * LEGACY - use fbAPIPromise
 * make actual Vue Resource call to a FB API URL
 * TODO: migrate everything new fbApiPromise and remove this
 * TODO: when migrating, hasValidationErrors = skipErrorModal
 *
 * @param callType string - get or post
 * @param url string - url to call after /api/v1
 * @param configOrBody - for get this is config, for post this is body
 * @param successCallback
 * @param errorCallback
 * @param hasValidationErrors boolean
 */
export const fbAPICall = (
  callType: string,
  url: string,
  configOrBody: Object,
  successCallback?: (res: any) => void,
  errorCallback?: (err: any) => void,
  hasValidationErrors?: boolean = false,
) => {
  const modalsStore = useModalsStore()

  // if hasValidationErrors then could be invalid data that should be shown to user
  // in which case the successCallback should handle.
  // otherwise any error is unexpected so just put up the ErrorModal
  const successFunction = (res) => {
    if (!hasValidationErrors && res.body.status === 'error') {
      // if error just show generic error modal and don't process data
      modalsStore.showErrorModal()

      if (typeof errorCallback !== 'undefined') {
        errorCallback(res)
      }
      return
    }
    if (typeof successCallback !== 'undefined') {
      successCallback(res)
    }
  }

  const errFunction = (err) => {
    if (err.status === 401) {
      modalsStore.showWasLoggedInModal()
    } else {
      modalsStore.showErrorModal()
    }

    if (typeof errorCallback !== 'undefined') {
      errorCallback(err)
    }
  }

  if (callType === 'get') {
    Vue.http.get(`/api/v1/` + url, configOrBody).then(successFunction, errFunction)
  } else if (callType === 'post') {
    Vue.http.post(`/api/v1/` + url, configOrBody).then(successFunction, errFunction)
  } else if (callType === 'postMulti') {
    const multipartHeader = { headers: { 'Content-Type': 'multipart/form-data' } }
    Vue.http
      .post(`/api/v1/` + url, configOrBody, multipartHeader)
      .then(successFunction, errFunction)
  } else if (callType === 'put') {
    Vue.http.put(`/api/v1/` + url, configOrBody).then(successFunction, errFunction)
  } else if (callType === 'delete') {
    Vue.http.delete(`/api/v1/` + url, configOrBody).then(successFunction, errFunction)
  }
}

/**
 * Promisified version of fbApiCall above, uses object destructuring to avoid
 * param order issues.
 *
 * If 400 csrf token error on logged-in user action, request will retry once.
 */
export const fbAPIPromise = async (
  { callType, url, body, config, skipErrorModal, prefix, attemptNum }: FbApiParams = {
    callType: 'get',
    url: '',
    body: null,
    config: null,
    headers: {},
    // allows skipping error modals (200 + status = error, 4xx and 5xx)
    skipErrorModal: false,
    prefix: 'api', // api|fbadmin
    attemptNum: 1, // used for retry failures
  },
): Promise<VueResourceResponse> => {
  const modalsStore = useModalsStore()
  const userAuthStore = useUserAuthStore()

  // default to public prefix if not set
  const endpointPrefix = prefix === 'fbadmin' ? '/fbadmin/api/v1' : '/api/v1'
  const endpoint = `${endpointPrefix}${url}`

  // multipart headers (inline type to stope flow errors)
  const configDefaults: {
    headers?: any,
  } = {
    ...config,
  }

  // add multipart headers
  if (callType === 'postMulti') {
    if (configDefaults.headers) {
      configDefaults.headers['Content-Type'] = 'multipart/form-data'
    } else {
      configDefaults.headers = { 'Content-Type': 'multipart/form-data' }
    }
  }

  // simple call types
  let httpResource

  if (['get', 'delete'].includes(callType)) {
    // simple calls, no body
    httpResource = Vue.http[callType](endpoint, configDefaults)
  } else if (['put', 'patch', 'post'].includes(callType)) {
    // include body
    httpResource = Vue.http[callType](endpoint, body, configDefaults)
  } else {
    // default if something crazy was passed in, do a get
    httpResource = Vue.http.get(endpoint, configDefaults)
  }

  // should have a valid http promise at this point
  return new Promise((resolve, reject) => {
    httpResource.then(
      // success
      (res: VueResourceResponse) => {
        // could be invalid data that should be shown to user
        // in which case the successCallback should handle.
        // otherwise any error is unexpected so just put up the ErrorModal
        if (!skipErrorModal && res.body.status === 'error') {
          // if error just show generic error modal and don't process data
          modalsStore.showErrorModal()

          // send to front end error tracker
          errorTracker.captureMessage({
            msg: `Vue API Error on Success`,
            extras: {
              url: res.url,
              statusCode: res.status,
              statusText: res.statusText,
              message: res.body && res.body.message ? res.body.message : '',
            },
          })

          return reject(res)
        }

        return resolve(res)
      },

      // error
      (res: VueResourceResponse) => {
        // no error tracker for auth errors
        if (res.status === 401) {
          modalsStore.showWasLoggedInModal()
          return reject(res)
        }

        // if delete request AND 404 no error tracking or modal
        if (callType && callType.toLowerCase() === 'delete' && res.status === 404) {
          return reject(res)
        }

        // force attempts to be set
        const attempts = attemptNum ?? 1

        // retry request if CSRF token error (only once)
        // seems to happen if logged-in user has long-running tab
        // NOTE: restrict to logged in only, anon users cannot hit the retry endpoint
        if (
          userAuthStore.user.isLoggedIn &&
          res.status === 400 &&
          res.body &&
          res.body.message === 'Token Error' &&
          attempts === 1
        ) {
          console.warn('token error, retrying')
          return retryCsrfToken({
            callType,
            url,
            body,
            config,
            skipErrorModal,
            prefix,
            attemptNum: attempts,
          })
            .then((retryRes: VueResourceResponse) => resolve(retryRes))
            .catch((retryRes: VueResourceResponse) => reject(retryRes))
        }

        // other 4xx or 5xx errors
        if (!skipErrorModal) {
          modalsStore.showErrorModal()
        }

        // send to front end error tracker
        errorTracker.captureMessage({
          msg: `Vue API Error`,
          extras: {
            url: res.url,
            statusCode: res.status,
            statusText: res.statusText,
            attemptNum: attempts,
            message: res.body && res.body.message ? res.body.message : '',
          },
        })

        return reject(res)
      },
    )
  })
}

/**
 * Retry any API request that failed due to CSRF error only once.
 *
 * Must be POST request to take advantage of session cookie
 * samesite: lax value.
 *
 * @see https://stackoverflow.com/questions/59990864/what-is-difference-between-samesite-lax-and-samesite-strict
 */
export const retryCsrfToken = async (params: FbApiParams) => {
  const userAuthStore = useUserAuthStore()

  // no try catch as an error should fail and return
  // should be unauthenticated 401 if that happens
  const res = await fbAPIPromise({
    callType: 'post',
    url: '/my/token',
    attemptNum: 2, // set only to skip any retry for this request
  })

  // assumes success
  // update pinia and window.laravel as their could be legacy code still referencing it
  userAuthStore.updateCsrfToken(res.body.data)
  // userAuthStore.updateCsrfToken('still-invalid');

  // set attempt number to 2 so no additional retries
  return fbAPIPromise({
    ...params,
    attemptNum: 2,
  })
}
